import { get } from 'lodash';

import { GetTableDataInput, ColumnType } from './graphql/types';
import { IViewResponse } from './types';
import {
  getWorkspaceData,
  getInitialTableData,
  getBatchedTableData,
  getTableData,
} from './network';
import { adaptWorkspaceData, adaptTableData, accumulateTableData, adaptOptions } from './adapter';
import { GetTableData_getTableData_columns } from '../../../graphql/core/types/GetTableData';
import { ResolvedType } from '../../types/util';
import { EXCLUDED_VIEW_TYPES } from '../../constants/core';

export const getWorkspaceInfo = async (workspaceId: string) => {
  const data = await getWorkspaceData({ id: workspaceId });
  return adaptWorkspaceData(data);
};

// retrieves data for the top level table used to initiate the file creator instance
export const getFirstLevelTable = async ({
  tableDataIds,
  accum,
}: {
  tableDataIds: GetTableDataInput;
  accum: IViewResponse;
}) => {
  const data = await getInitialTableData(tableDataIds);
  // options contain info on which views are available
  const options = adaptOptions(data);
  const adaptedAccum = adaptTableData(data, accum, true);
  return { allAdaptedViews: adaptedAccum, allAdaptedOptions: options };
};

// retrieves data for a single table
// used when a rec ref column is added and the nested ref table is missing data
export const getTable = async ({
  tableParams,
  accum,
}: {
  tableParams: GetTableDataInput;
  accum: IViewResponse;
}): Promise<any> => {
  const data = await getTableData(tableParams);
  return adaptTableData(data, accum);
};

// retrieves data for multiple tables
// used when initiating file creator instance with existing spaces that reference foreign tables
export const getBatchedTables = async ({
  workspaceId,
  blockId,
  tableSet,
  accum,
}: {
  workspaceId: string;
  blockId: string;
  tableSet: string[];
  accum: IViewResponse;
}) => {
  const tableDataIds = tableSet.map((tableId) => ({ workspaceId, tableId, blockId }));
  const data = await getBatchedTableData(tableDataIds);
  return accumulateTableData(data, accum);
};

export const checkIfLookupIsLookingAtRecordReference = (field: GetTableData_getTableData_columns) =>
  get(field, 'typeOptions.lookupColumnType') === ColumnType.RECORD_REFERENCE;

// The default view can be an excluded view type sometimes.
// Thus, we must find the next grid view to use as the default view for file creator.
// At least one grid view must exist for any given table.
export const getDefaultGridViewId = (
  { allAdaptedViews, allAdaptedOptions }: ResolvedType<ReturnType<typeof getFirstLevelTable>>,
  tableId: string,
) => {
  // The default view ID as provided by back end
  const defaultViewId = allAdaptedViews.tableById[tableId].defaultViewId;
  // The view options contain the type of a view. viewOptionById is already filtered so
  // that it does not contain any excluded views.
  const viewOptionById = allAdaptedOptions.viewOptionById;

  // If the back-end-provided defaultViewId exists in viewOptionById, then it must not
  // be aan excluded view.
  if (allAdaptedOptions.viewOptionById[defaultViewId]) return defaultViewId;

  // Find the first non-excluded view in viewOptionById and return its ID to use as default.
  const nonExcludedView = Object.values(viewOptionById).find(
    (viewOption) => EXCLUDED_VIEW_TYPES[viewOption.type] === false,
  );

  return nonExcludedView!.id;
};

export const isDisabledOrDeletedSyncTable = (
  syncTableMeta?: { isEnabled: boolean | null; isSourceTableDeleted: boolean | null } | null,
) => {
  if (!syncTableMeta) return false;
  const { isEnabled, isSourceTableDeleted } = syncTableMeta;
  if (!isEnabled || isSourceTableDeleted) return true;
  return false;
};
